import { useQuery } from '@tanstack/react-query';
import { DashboardApi } from '../api';
import type {
  DashboardActivityTableResponse,
  DashboardActivityTableSortColumn,
  DashboardActivityTableSortDirection,
} from '../../interfaces/dashboardLeaderboardActivity';

const dashboardApi = new DashboardApi();

export const DEFAULT_ACTIVITY_PAGE_SIZE = 20;

const inJest = typeof process !== 'undefined' && process.env.JEST_WORKER_ID != null;

export type UseDashboardActivityTableParams = {
  page: number;
  pageSize?: number;
  teamId?: string | null;
  playersGroup?: string;
  search?: string;
  sortColumn?: DashboardActivityTableSortColumn | null;
  sortDirection?: DashboardActivityTableSortDirection;
  enabled?: boolean;
};

export const buildDashboardActivityTableQueryKey = (p: UseDashboardActivityTableParams) =>
  [
    'dashboard-activity-table',
    p.page,
    p.pageSize ?? DEFAULT_ACTIVITY_PAGE_SIZE,
    p.teamId ?? '',
    p.playersGroup ?? '',
    p.search ?? '',
    p.sortColumn ?? '',
    p.sortColumn ? (p.sortDirection ?? 'desc') : '',
  ] as const;

export const useDashboardActivityTable = ({
  page,
  pageSize = DEFAULT_ACTIVITY_PAGE_SIZE,
  teamId,
  playersGroup = '',
  search = '',
  sortColumn = null,
  sortDirection = 'desc',
  enabled = true,
}: UseDashboardActivityTableParams) => {
  return useQuery<DashboardActivityTableResponse>({
    queryKey: buildDashboardActivityTableQueryKey({
      page,
      pageSize,
      teamId,
      playersGroup,
      search,
      sortColumn,
      sortDirection,
    }),
    queryFn: async () => {
      const base = {
        page,
        pageSize,
        teamId,
        playersGroup,
        search,
        sortColumn,
        sortDirection: sortColumn ? sortDirection : undefined,
      };
      const res = inJest
        ? await dashboardApi.getActivityTableMock(base)
        : await dashboardApi.getActivityTable(base);
      if (!res.status) throw new Error('Failed to load activity table');
      return res.data;
    },
    enabled,
    placeholderData: previous => previous,
  });
};
